import { useCallback, useMemo } from 'react';
import {
  SignalCategorySchema,
  SignalOriginSchema,
  SignalStageSchema,
  type SignalCategoryType,
  type SignalOriginType,
  type SignalStageType,
} from '@terra/shared-schemas-orchestrator';
import {
  ALL_SIGNAL_STATUS_FILTER_VALUES,
  CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER,
  SignalListGroupByFieldSchema,
  SignalSourceFilterValueSchema,
  splitSignalStatusFilterValues,
  type SignalListGroupByField,
  type SignalListSortField,
  type SignalSourceFilterValue,
  type SignalStatusFilterValue,
} from '@terra/shared-contracts-signals';
import {
  ConfidenceLevelSchema,
  type ConfidenceLevelType,
} from '../../types/confidence';
import {
  DEFAULT_LIFECYCLE_STATE_FILTER,
  type LifecycleStateFilterDto,
} from '@terra/shared-dto-types';
import { createTabScopedStore } from './createTabScopedStore';

export {
  CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER,
  type SignalStatusFilterValue,
};

const SIGNAL_STAGE_VALUES = new Set<string>(SignalStageSchema.options);
const SIGNAL_CATEGORY_VALUES = new Set<string>(SignalCategorySchema.options);
const SIGNAL_ORIGIN_FILTER_VALUES = new Set<string>(
  SignalOriginSchema.options.filter(
    (origin) => origin !== 'MANUAL' && origin !== 'SCANNER_IMPORT',
  ),
);
const CONFIDENCE_VALUES = new Set<string>(ConfidenceLevelSchema.options);

function normalizeEnumArray<TValue extends string>({
  value,
  allowedValues,
}: {
  value: unknown;
  allowedValues: ReadonlySet<string>;
}): TValue[] {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : [];

  return values.filter(
    (item): item is TValue =>
      typeof item === 'string' && item !== 'ALL' && allowedValues.has(item),
  );
}

function normalizeAssigneeArray(value: unknown): SignalAssignee {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : [];

  return values.filter(
    (item): item is SignalAssignee[number] =>
      typeof item === 'string' && item !== '' && item !== 'ALL',
  );
}

export type SignalConfidence = ConfidenceLevelType[];
export type SignalCategory = SignalCategoryType[];
export type SignalOrigin = SignalOriginType[];
export type SignalSourceFilter = SignalSourceFilterValue[];
export type SignalStageFilter = SignalStageType[];
export type SignalStatusFilter = SignalStatusFilterValue[];
export type SignalAssignee = Array<'UNASSIGNED' | string>;
export type SignalExecutionFilter = 'ALL' | 'WITH' | 'WITHOUT';
export type SignalFindingFilter = 'ALL' | 'WITH' | 'WITHOUT';
export type SignalSecurityTestFilter = 'ALL' | 'WITH' | 'WITHOUT';
export type SignalAppWideFilter = 'ALL' | 'WITH' | 'WITHOUT';
export type SignalsSortField = SignalListSortField;
export type SignalsSortOrder = 'asc' | 'desc';
export type SignalsListGrouping = {
  field: SignalListGroupByField;
  order: SignalsSortOrder;
} | null;

export const DEFAULT_SIGNALS_GROUP_SORT_ORDER: SignalsSortOrder = 'asc';

const normalizeGrouping = (value: unknown): SignalsListGrouping => {
  if (value === null || typeof value !== 'object') {
    return null;
  }

  const field = SignalListGroupByFieldSchema.safeParse(
    Reflect.get(value, 'field'),
  );

  return field.success
    ? {
        field: field.data,
        order:
          Reflect.get(value, 'order') === 'desc'
            ? 'desc'
            : DEFAULT_SIGNALS_GROUP_SORT_ORDER,
      }
    : null;
};

export type SignalsTabFilterState = {
  searchTerm: string;
  urlSearchTerm: string;
  stageFilter: SignalStageFilter;
  statusFilter: SignalStatusFilter;
  confidenceFilter: SignalConfidence;
  categoryFilter: SignalCategory;
  originFilter: SignalOrigin;
  sourceFilter: SignalSourceFilter;
  assigneeFilter: SignalAssignee;
  hasExecutionFilter: SignalExecutionFilter;
  hasFindingFilter: SignalFindingFilter;
  securityTestFilter: SignalSecurityTestFilter;
  appWideFilter: SignalAppWideFilter;
  sortField: SignalsSortField;
  sortOrder: SignalsSortOrder;
  grouping: SignalsListGrouping;
  maxTests: number;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  createdSince: Date | null;
  testedStartDate: Date | null;
  testedEndDate: Date | null;
  viewRejectedSignals: boolean;
  lifecycleState: LifecycleStateFilterDto;
  selectedIds: string[];
  excludedIds: string[];
  isAllFilteredSelected: boolean;
  expandedSignalIds: string[];
};

const normalizeIdArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item): item is string => typeof item === 'string' && item !== '',
  );
};

const SIGNAL_TAB_FILTER_DEFAULTS = {
  searchTerm: '',
  urlSearchTerm: '',
  stageFilter: [],
  statusFilter: [],
  confidenceFilter: [],
  categoryFilter: [],
  originFilter: [],
  sourceFilter: [],
  assigneeFilter: [],
  hasExecutionFilter: 'ALL',
  hasFindingFilter: 'ALL',
  securityTestFilter: 'ALL',
  appWideFilter: 'ALL',
  sortField: 'createdAt',
  sortOrder: 'desc',
  grouping: null,
  maxTests: 5,
  createdStartDate: null,
  createdEndDate: null,
  createdSince: null,
  testedStartDate: null,
  testedEndDate: null,
  viewRejectedSignals: false,
  lifecycleState: DEFAULT_LIFECYCLE_STATE_FILTER,
  selectedIds: [],
  excludedIds: [],
  isAllFilteredSelected: false,
  expandedSignalIds: [],
} satisfies SignalsTabFilterState;

const useSignalsTabScopedFilterStoreBase =
  createTabScopedStore<SignalsTabFilterState>(SIGNAL_TAB_FILTER_DEFAULTS);

export function useTabScopedSignalsFilters() {
  const { filters, update, clearTabScope } =
    useSignalsTabScopedFilterStoreBase();
  const stageFilter = normalizeEnumArray<SignalStageType>({
    value: filters.stageFilter,
    allowedValues: SIGNAL_STAGE_VALUES,
  });
  const statusFilter = normalizeEnumArray<SignalStatusFilterValue>({
    value: filters.statusFilter,
    allowedValues: ALL_SIGNAL_STATUS_FILTER_VALUES,
  });
  const splitStatusFilter = useMemo(
    () => splitSignalStatusFilterValues(statusFilter),
    [statusFilter],
  );
  const confidenceFilter = normalizeEnumArray<ConfidenceLevelType>({
    value: filters.confidenceFilter,
    allowedValues: CONFIDENCE_VALUES,
  });
  const categoryFilter = normalizeEnumArray<SignalCategoryType>({
    value: filters.categoryFilter,
    allowedValues: SIGNAL_CATEGORY_VALUES,
  });
  const originFilter = normalizeEnumArray<SignalOriginType>({
    value: filters.originFilter,
    allowedValues: SIGNAL_ORIGIN_FILTER_VALUES,
  });
  const sourceFilter = normalizeEnumArray<SignalSourceFilterValue>({
    value: filters.sourceFilter,
    allowedValues: new Set<string>(SignalSourceFilterValueSchema.options),
  });
  const assigneeFilter = normalizeAssigneeArray(filters.assigneeFilter);
  const expandedSignalIds = normalizeIdArray(filters.expandedSignalIds);
  const grouping = useMemo(
    () => normalizeGrouping(filters.grouping),
    [filters.grouping],
  );

  const setSearchTerm = useCallback(
    (searchTerm: string) => update({ searchTerm }),
    [update],
  );
  const setUrlSearchTerm = useCallback(
    (urlSearchTerm: string) => update({ urlSearchTerm }),
    [update],
  );
  const setStageFilter = useCallback(
    (stageFilter: SignalStageFilter) =>
      update({
        stageFilter: normalizeEnumArray<SignalStageType>({
          value: stageFilter,
          allowedValues: SIGNAL_STAGE_VALUES,
        }),
      }),
    [update],
  );
  const setStatusFilter = useCallback(
    (statusFilter: SignalStatusFilter) =>
      update({
        statusFilter: normalizeEnumArray<SignalStatusFilterValue>({
          value: statusFilter,
          allowedValues: ALL_SIGNAL_STATUS_FILTER_VALUES,
        }),
      }),
    [update],
  );
  const setConfidenceFilter = useCallback(
    (confidenceFilter: SignalConfidence) =>
      update({
        confidenceFilter: normalizeEnumArray<ConfidenceLevelType>({
          value: confidenceFilter,
          allowedValues: CONFIDENCE_VALUES,
        }),
      }),
    [update],
  );
  const setCategoryFilter = useCallback(
    (categoryFilter: SignalCategory) =>
      update({
        categoryFilter: normalizeEnumArray<SignalCategoryType>({
          value: categoryFilter,
          allowedValues: SIGNAL_CATEGORY_VALUES,
        }),
      }),
    [update],
  );
  const setOriginFilter = useCallback(
    (originFilter: SignalOrigin) =>
      update({
        originFilter: normalizeEnumArray<SignalOriginType>({
          value: originFilter,
          allowedValues: SIGNAL_ORIGIN_FILTER_VALUES,
        }),
      }),
    [update],
  );
  const setSourceFilter = useCallback(
    (sourceFilter: SignalSourceFilter) =>
      update({
        sourceFilter: normalizeEnumArray<SignalSourceFilterValue>({
          value: sourceFilter,
          allowedValues: new Set<string>(SignalSourceFilterValueSchema.options),
        }),
      }),
    [update],
  );
  const setAssigneeFilter = useCallback(
    (assigneeFilter: SignalAssignee) =>
      update({ assigneeFilter: normalizeAssigneeArray(assigneeFilter) }),
    [update],
  );
  const setHasExecutionFilter = useCallback(
    (hasExecutionFilter: SignalExecutionFilter) =>
      update({ hasExecutionFilter }),
    [update],
  );
  const setHasFindingFilter = useCallback(
    (hasFindingFilter: SignalFindingFilter) => update({ hasFindingFilter }),
    [update],
  );
  const setSecurityTestFilter = useCallback(
    (securityTestFilter: SignalSecurityTestFilter) =>
      update({ securityTestFilter }),
    [update],
  );
  const setAppWideFilter = useCallback(
    (appWideFilter: SignalAppWideFilter) => update({ appWideFilter }),
    [update],
  );
  const setSortField = useCallback(
    (sortField: SignalsSortField) => update({ sortField }),
    [update],
  );
  const setSortOrder = useCallback(
    (sortOrder: SignalsSortOrder) => update({ sortOrder }),
    [update],
  );
  const setGroupByField = useCallback(
    (field: SignalListGroupByField | null) =>
      update({
        grouping: field
          ? { field, order: DEFAULT_SIGNALS_GROUP_SORT_ORDER }
          : null,
      }),
    [update],
  );
  const setSortState = useCallback(
    ({
      sort,
      grouping: nextGrouping,
    }: {
      sort: { field: SignalsSortField; order: SignalsSortOrder };
      grouping: SignalsListGrouping;
    }) =>
      update({
        sortField: sort.field,
        sortOrder: sort.order,
        grouping: normalizeGrouping(nextGrouping),
      }),
    [update],
  );
  const setMaxTests = useCallback(
    (maxTests: number) => update({ maxTests }),
    [update],
  );
  const setCreatedStartDate = useCallback(
    (createdStartDate: Date | null) => update({ createdStartDate }),
    [update],
  );
  const setCreatedEndDate = useCallback(
    (createdEndDate: Date | null) => update({ createdEndDate }),
    [update],
  );
  const setCreatedSince = useCallback(
    (createdSince: Date | null) => update({ createdSince }),
    [update],
  );
  const setTestedStartDate = useCallback(
    (testedStartDate: Date | null) => update({ testedStartDate }),
    [update],
  );
  const setTestedEndDate = useCallback(
    (testedEndDate: Date | null) => update({ testedEndDate }),
    [update],
  );
  const setViewRejectedSignals = useCallback(
    (viewRejectedSignals: boolean) => update({ viewRejectedSignals }),
    [update],
  );
  const setLifecycleState = useCallback(
    (lifecycleState: LifecycleStateFilterDto) => update({ lifecycleState }),
    [update],
  );
  const setSelectedIds = useCallback(
    (selectedIds: string[]) => update({ selectedIds }),
    [update],
  );
  const setExcludedIds = useCallback(
    (excludedIds: string[]) => update({ excludedIds }),
    [update],
  );
  const setIsAllFilteredSelected = useCallback(
    (isAllFilteredSelected: boolean) => update({ isAllFilteredSelected }),
    [update],
  );
  const clearSelection = useCallback(() => {
    update({
      selectedIds: [],
      excludedIds: [],
      isAllFilteredSelected: false,
    });
  }, [update]);
  const setExpandedSignalIds = useCallback(
    (expandedSignalIds: string[]) =>
      update({ expandedSignalIds: normalizeIdArray(expandedSignalIds) }),
    [update],
  );
  const resetFilters = useCallback(() => {
    update({
      ...SIGNAL_TAB_FILTER_DEFAULTS,
      expandedSignalIds,
      grouping,
    });
  }, [expandedSignalIds, grouping, update]);

  return {
    ...filters,
    stageFilter,
    statusFilter,
    signalStatusFilter: splitStatusFilter.status ?? [],
    confirmedVulnerabilityFindingStateFilter:
      splitStatusFilter.confirmedVulnerabilityFindingState ?? [],
    blockedReasonFilter: splitStatusFilter.blockedReason ?? [],
    confidenceFilter,
    categoryFilter,
    originFilter,
    sourceFilter,
    assigneeFilter,
    grouping,
    setSearchTerm,
    setUrlSearchTerm,
    setStageFilter,
    setStatusFilter,
    setConfidenceFilter,
    setCategoryFilter,
    setOriginFilter,
    setSourceFilter,
    setAssigneeFilter,
    setHasExecutionFilter,
    setHasFindingFilter,
    setSecurityTestFilter,
    setAppWideFilter,
    setSortField,
    setSortOrder,
    setGroupByField,
    setSortState,
    setMaxTests,
    setCreatedStartDate,
    setCreatedEndDate,
    setCreatedSince,
    setTestedStartDate,
    setTestedEndDate,
    setViewRejectedSignals,
    setLifecycleState,
    setSelectedIds,
    setExcludedIds,
    setIsAllFilteredSelected,
    clearSelection,
    expandedSignalIds,
    setExpandedSignalIds,
    resetFilters,
    clearTabScope,
  };
}
